import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export const CM_TO_M = 0.01

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  }).format(amount)
}

// ---------------------------------------------------------------------------
// Vertical Bracing helpers
// ---------------------------------------------------------------------------

// Maximum total load per row used in the double-sided Rule 2 calculation.
const MAX_LOAD_PER_ROW = 24000

/**
 * Calculates the number of vertical bracing units required.
 *
 * Single-sided:
 *   Always 1 bracing per 8 sections (ceiling division).
 *
 * Double-sided:
 *   Rule 1: 1 per 8 sections (ceiling division).
 *   Rule 2: ceil((shelfLoad * sections * shelfLevels * 2) / MAX_LOAD_PER_ROW)
 *   Result : ceiling of the maximum of Rule 1 and Rule 2.
 *
 * @param sectionCount  Total number of bay sections.
 * @param sides         "single" or "double".
 * @param shelfLoad     Load capacity per shelf (kg).
 * @param shelfLevels   Number of shelf levels per section.
 */
export function calculateBracingCount(
  sectionCount: number,
  sides: "single" | "double",
  shelfLoad: number,
  shelfLevels: number
): number {
  // Rule 1: 1 bracing per 8 sections (ceiling division applies to both modes)
  const rule1 = Math.ceil(sectionCount / 8)

  if (sides === "single") {
    return Math.max(1, rule1)
  }

  // Double-sided Rule 2: total load across both faces / max load per row
  const rule2 = Math.ceil(
    (shelfLoad * sectionCount * shelfLevels * 2) / MAX_LOAD_PER_ROW
  )

  return Math.max(1, Math.ceil(Math.max(rule1, rule2)))
}

/**
 * Returns the 0-based section indices where vertical bracings should be placed.
 *
 * Placement rules:
 *   1 bracing, sectionCount = 1 → place at index 0 (only section available).
 *   1 bracing, sectionCount > 1 → place at index 1 (second section). Bracings
 *     always start from the second section unless there is only one section.
 *   2 bracings → place at index 1 (second section) and index sectionCount-2
 *     (second-last section).
 *   3+ bracings → always use the second section (index 1) and the second-last
 *     section (index sectionCount-2), with the remaining bracings equally
 *     distributed in between.
 *
 * The "minimum 1 per 8 sections" rule is enforced by calculateBracingCount.
 * This function only handles placement of however many bracings are required.
 *
 * @param bracingCount  Number of bracing units (from calculateBracingCount).
 * @param sectionCount  Total number of bay sections.
 */
export function calculateBracingPositions(
  bracingCount: number,
  sectionCount: number
): number[] {
  if (bracingCount <= 0 || sectionCount <= 0) return []

  // With only 1 section there is nowhere else to put it – use index 0.
  if (sectionCount === 1) return [0]

  // For all multi-section configurations the first bracing always starts at
  // the second section (index 1), never at the first.
  const firstAnchor = 1
  // Second-last section (index sectionCount - 2); for a 2-section run this
  // equals firstAnchor so we clamp to avoid duplicates.
  const lastAnchor = Math.max(firstAnchor, sectionCount - 2)

  if (bracingCount === 1) {
    // Single bracing: place at the second section (index 1).
    return [firstAnchor]
  }

  if (bracingCount === 2 || firstAnchor >= lastAnchor) {
    // Two bracings (or not enough span to spread): second and second-last.
    // When firstAnchor === lastAnchor only one unique position exists.
    return firstAnchor === lastAnchor ? [firstAnchor] : [firstAnchor, lastAnchor]
  }

  // 3 or more bracings:
  // Fixed anchors at second section and second-last section.
  // The remaining (bracingCount - 2) bracings are distributed evenly between
  // these two anchors.
  const positions = new Set<number>()
  positions.add(firstAnchor)
  positions.add(lastAnchor)

  const middleCount = bracingCount - 2
  if (middleCount > 0) {
    // Divide the span into (middleCount + 1) equal intervals and round to
    // the nearest whole section index.
    const span = lastAnchor - firstAnchor
    for (let i = 1; i <= middleCount; i++) {
      const pos = Math.round(firstAnchor + (span * i) / (middleCount + 1))
      positions.add(pos)
    }
  }

  return Array.from(positions).sort((a, b) => a - b)
}
